import { AsyncOperationStatus } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { AsyncOperationHandleView } from "./types";

interface AsyncOperationProgressProps {
    handle: AsyncOperationHandleView;
    percentComplete: number | null;
    pendingLabel: string;
    cancelLabel: string;
    cancelingLabel: string;
    onCancel: () => void;
    isCanceling: boolean;
}

const ProgressBar: React.FC<{ percentComplete: number }> = ({ percentComplete }) => (
    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
        <div
            className="h-full rounded-full bg-brand-600 transition-all duration-300"
            style={{ width: `${percentComplete}%` }}
        />
    </div>
);

export const AsyncOperationProgress: React.FC<AsyncOperationProgressProps> = ({
    handle,
    percentComplete,
    pendingLabel,
    cancelLabel,
    cancelingLabel,
    onCancel,
    isCanceling,
}) => {
    if (handle.status === AsyncOperationStatus.Failed) {
        return (
            <InlineBanner type="error">
                {handle.errorMessage ?? "This did not finish. Please try again."}
            </InlineBanner>
        );
    }

    if (handle.status === AsyncOperationStatus.Canceled) {
        return <InlineBanner type="info">Canceled.</InlineBanner>;
    }

    if (handle.status === AsyncOperationStatus.Succeeded) {
        return null;
    }

    const detail = handle.progress.message ?? pendingLabel;

    return (
        <div className="space-y-2">
            <div className="flex items-center space-x-2">
                <Spinner />
                <span className="text-sm text-gray-700">{detail}</span>
            </div>
            {percentComplete === null ? null : (
                <ProgressBar percentComplete={percentComplete} />
            )}
            <SecondaryButton onClick={onCancel} disabled={isCanceling}>
                {isCanceling ? cancelingLabel : cancelLabel}
            </SecondaryButton>
        </div>
    );
};
